/**
 * Tenant Branding API Client (Story 14.5 — AC #8)
 *
 * Typed wrappers around the three /api/tenant/branding endpoints. Uses the
 * authenticated `apiClient` — these are agency-side configuration endpoints,
 * NOT the anonymous portal-public surface.
 */

import { apiGet, apiPatch, apiPost } from "./apiClient";

export interface TenantBrandingProjection {
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  companyName: string | null;
  // Epic 15 white-label fields — readable as of Story 15.1; write paths land in
  // their feature stories (faviconUrl → 15.2, customEmailDomain/supportEmail →
  // 15.4, helpUrl → 15.7).
  faviconUrl: string | null;
  customEmailDomain: string | null;
  supportEmail: string | null;
  helpUrl: string | null;
  /** Writable as of Story 7.10 — white-label footer text (PDF + email footer). */
  footerText: string | null;
  /** Writable as of Story 7.10 — email sender display name (report From). */
  fromName: string | null;
  /** ISO datetime — required on the next PATCH for optimistic concurrency. */
  updatedAt: string;
}

export interface TenantBrandingUpdatePatch {
  logoUrl?: string | null;
  /** Writable as of Story 15.2 (tenant-scoped favicon URL, guarded server-side). */
  faviconUrl?: string | null;
  /** Writable as of Story 15.7 (agency docs URL; https-only). */
  helpUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  companyName?: string | null;
  /** Writable as of Story 7.10 — white-label footer text. */
  footerText?: string | null;
  /** Writable as of Story 7.10 — email sender display name. */
  fromName?: string | null;
  /** From the last GET. The server rejects the write when this is stale. */
  updatedAt: string;
}

export interface TenantBrandingUpdateResult {
  data: TenantBrandingProjection;
  warnings?: string[];
}

export interface LogoUploadUrlResult {
  uploadUrl: string;
  logoUrl: string;
  expiresIn: number;
}

export interface FaviconUploadUrlResult {
  uploadUrl: string;
  faviconUrl: string;
  expiresIn: number;
}

/**
 * Typed error for the stale-update 409 case so callers can surface a
 * "reload branding" action in the UI (persona-4 feedback loop).
 */
export class TenantBrandingConflictError extends Error {
  status = 409 as const;
  constructor(message: string) {
    super(message);
    this.name = "TenantBrandingConflictError";
  }
}

/**
 * Typed error for 400 validation failures so callers can surface
 * field-level errors to the form.
 */
export class TenantBrandingValidationError extends Error {
  status = 400 as const;
  fields: Array<{ field: string; message: string }>;
  constructor(
    message: string,
    fields: Array<{ field: string; message: string }> = [],
  ) {
    super(message);
    this.name = "TenantBrandingValidationError";
    this.fields = fields;
  }
}

interface ProblemBody {
  title?: string;
  detail?: string;
  errors?: Array<{ field: string; message: string }>;
  type?: string;
}

async function readErrorBody(response: Response): Promise<ProblemBody> {
  try {
    return (await response.json()) as ProblemBody;
  } catch {
    return {};
  }
}

async function assertOk(response: Response, label: string): Promise<void> {
  if (response.ok) return;
  if (response.status === 401) {
    throw new Error(`${label}: not authenticated`);
  }
  const body = await readErrorBody(response);
  if (response.status === 409) {
    throw new TenantBrandingConflictError(
      body.detail ?? body.title ?? "Branding was modified by another user.",
    );
  }
  if (response.status === 400) {
    throw new TenantBrandingValidationError(
      body.detail ?? body.title ?? "Validation failed",
      body.errors ?? [],
    );
  }
  throw new Error(
    `${label}: HTTP ${response.status} — ${body.detail ?? body.title ?? "unknown error"}`,
  );
}

/**
 * Runtime guard: ensures an unknown response has the branding-projection shape.
 * Protects against malformed JSON silently satisfying TypeScript and crashing
 * downstream UI code (CR1 M6). PURE — validates only, never mutates `value`;
 * normalization of absent Epic-15 fields happens in `normalizeBrandingProjection`.
 *
 * Strict/lenient asymmetry is deliberate: the original four fields predate every
 * deployed server, so an absent one means a CORRUPT response → throw. The four
 * Epic-15 fields can be legitimately absent from a pre-15.1 server during deploy
 * skew → tolerate absence (defaulted to null downstream); reject only a
 * present-but-wrong-typed value.
 */
function assertBrandingProjection(
  value: unknown,
): asserts value is TenantBrandingProjection {
  if (!value || typeof value !== "object") {
    throw new Error("Malformed branding response: expected object");
  }
  // CR3 R3-F2: `typeof [] === "object"` so the previous guard let arrays
  // slip through. Explicitly reject arrays so downstream field reads can
  // trust the shape.
  if (Array.isArray(value)) {
    throw new Error("Malformed branding response: expected object, got array");
  }
  const v = value as Record<string, unknown>;
  for (const field of ["logoUrl", "primaryColor", "secondaryColor", "companyName"] as const) {
    if (v[field] !== null && typeof v[field] !== "string") {
      throw new Error(
        `Malformed branding response: ${field} must be string or null`,
      );
    }
  }
  for (const field of ["faviconUrl", "customEmailDomain", "supportEmail", "helpUrl", "footerText", "fromName"] as const) {
    if (v[field] !== undefined && v[field] !== null && typeof v[field] !== "string") {
      throw new Error(
        `Malformed branding response: ${field} must be string or null`,
      );
    }
  }
  if (typeof v.updatedAt !== "string") {
    throw new Error("Malformed branding response: updatedAt must be string");
  }
}

/**
 * Build a fresh, complete projection. Defaults the (possibly-absent under deploy
 * skew) Epic-15 fields to null so the non-optional `TenantBrandingProjection`
 * type holds at runtime — without mutating the parsed response object.
 */
function normalizeBrandingProjection(
  data: TenantBrandingProjection,
): TenantBrandingProjection {
  return {
    logoUrl: data.logoUrl ?? null,
    primaryColor: data.primaryColor ?? null,
    secondaryColor: data.secondaryColor ?? null,
    companyName: data.companyName ?? null,
    faviconUrl: data.faviconUrl ?? null,
    customEmailDomain: data.customEmailDomain ?? null,
    supportEmail: data.supportEmail ?? null,
    helpUrl: data.helpUrl ?? null,
    footerText: data.footerText ?? null,
    fromName: data.fromName ?? null,
    updatedAt: data.updatedAt,
  };
}

export async function fetchTenantBranding(): Promise<TenantBrandingProjection> {
  const response = await apiGet("/api/tenant/branding");
  await assertOk(response, "Fetch tenant branding");
  const json = (await response.json()) as { data?: unknown };
  assertBrandingProjection(json.data);
  return normalizeBrandingProjection(json.data);
}

export async function updateTenantBranding(
  patch: TenantBrandingUpdatePatch,
): Promise<TenantBrandingUpdateResult> {
  const response = await apiPatch("/api/tenant/branding", patch);
  await assertOk(response, "Update tenant branding");
  const json = (await response.json()) as {
    data?: unknown;
    warnings?: unknown;
  };
  assertBrandingProjection(json.data);
  const warnings = Array.isArray(json.warnings)
    ? json.warnings.filter((w): w is string => typeof w === "string")
    : undefined;
  return { data: normalizeBrandingProjection(json.data), warnings };
}

export async function requestLogoUploadUrl(params: {
  contentType: string;
  fileSize: number;
}): Promise<LogoUploadUrlResult> {
  const response = await apiPost(
    "/api/tenant/branding/logo/upload-url",
    params,
  );
  await assertOk(response, "Request logo upload URL");
  const json = (await response.json()) as { data?: unknown };
  const data = json.data as LogoUploadUrlResult | undefined;
  if (
    !data ||
    typeof data.uploadUrl !== "string" ||
    typeof data.logoUrl !== "string" ||
    typeof data.expiresIn !== "number"
  ) {
    throw new Error("Malformed upload-url response");
  }
  return data;
}

export async function requestFaviconUploadUrl(params: {
  contentType: string;
  fileSize: number;
}): Promise<FaviconUploadUrlResult> {
  const response = await apiPost(
    "/api/tenant/branding/favicon/upload-url",
    params,
  );
  await assertOk(response, "Request favicon upload URL");
  const json = (await response.json()) as { data?: unknown };
  const data = json.data as FaviconUploadUrlResult | undefined;
  if (
    !data ||
    typeof data.uploadUrl !== "string" ||
    typeof data.faviconUrl !== "string" ||
    typeof data.expiresIn !== "number"
  ) {
    throw new Error("Malformed favicon upload-url response");
  }
  return data;
}

// ============================================================================
// Custom Email Sender Domain (Story 15.4 — Agency)
// ============================================================================

export type EmailDomainStatus = "pending" | "verifying" | "verified" | "failed";

export interface EmailSenderDnsRecord {
  type: "CNAME";
  /** Host/name field to publish, e.g. `tok1._domainkey.mail.acme.com`. */
  name: string;
  /** Value/target field, e.g. `tok1.dkim.amazonses.com`. */
  value: string;
}

export interface EmailSenderProjection {
  customEmailDomain: string | null;
  customEmailDomainStatus: EmailDomainStatus | null;
  supportEmail: string | null;
  /** The DKIM CNAME records the agency must publish (empty when no domain). */
  dnsRecords: EmailSenderDnsRecord[];
  /** ISO datetime the domain first verified, or null. */
  verifiedAt: string | null;
  /** ISO datetime — required on the next PATCH for optimistic concurrency. */
  updatedAt: string;
}

export interface EmailSenderPatch {
  /** omit → preserve; null → clear; string → set the custom sender domain. */
  customEmailDomain?: string | null;
  /** omit → preserve; null → clear; string → set the reply-to support email. */
  supportEmail?: string | null;
  /** From the last GET. The server rejects the write when this is stale. */
  updatedAt: string;
}

function assertEmailSenderProjection(
  value: unknown,
): asserts value is EmailSenderProjection {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Malformed email-sender response: expected object");
  }
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.dnsRecords)) {
    throw new Error("Malformed email-sender response: dnsRecords must be an array");
  }
  if (typeof v.updatedAt !== "string") {
    throw new Error("Malformed email-sender response: updatedAt must be string");
  }
}

export async function fetchEmailSender(): Promise<EmailSenderProjection> {
  const response = await apiGet("/api/tenant/branding/email-sender");
  await assertOk(response, "Fetch email sender domain");
  const json = (await response.json()) as { data?: unknown };
  assertEmailSenderProjection(json.data);
  return json.data;
}

export async function updateEmailSender(
  patch: EmailSenderPatch,
): Promise<EmailSenderProjection> {
  const response = await apiPatch("/api/tenant/branding/email-sender", patch);
  await assertOk(response, "Update email sender domain");
  const json = (await response.json()) as { data?: unknown };
  assertEmailSenderProjection(json.data);
  return json.data;
}

export async function verifyEmailSender(): Promise<EmailSenderProjection> {
  const response = await apiPost(
    "/api/tenant/branding/email-sender/verify",
    {},
  );
  await assertOk(response, "Verify email sender domain");
  const json = (await response.json()) as { data?: unknown };
  assertEmailSenderProjection(json.data);
  return json.data;
}

// ============================================================================
// Custom Portal Domain (Story 15.5 — Agency)
// ============================================================================

export type PortalDomainStatus = "pending" | "verifying" | "verified" | "failed";

export interface PortalDnsRecord {
  type: "CNAME";
  name: string;
  value: string;
}

export interface PortalDomainProjection {
  customDomain: string | null;
  customDomainStatus: PortalDomainStatus | null;
  /** The single CNAME the agency must publish (empty when no domain). */
  dnsRecords: PortalDnsRecord[];
  verifiedAt: string | null;
  /** ISO datetime — required on the next PATCH for optimistic concurrency. */
  updatedAt: string;
}

export interface PortalDomainPatch {
  /** omit → preserve; null → clear; string → set the custom portal domain. */
  customDomain?: string | null;
  updatedAt: string;
}

function assertPortalDomainProjection(
  value: unknown,
): asserts value is PortalDomainProjection {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Malformed portal-domain response: expected object");
  }
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.dnsRecords)) {
    throw new Error("Malformed portal-domain response: dnsRecords must be an array");
  }
  if (typeof v.updatedAt !== "string") {
    throw new Error("Malformed portal-domain response: updatedAt must be string");
  }
}

export async function fetchPortalDomain(): Promise<PortalDomainProjection> {
  const response = await apiGet("/api/tenant/branding/portal-domain");
  await assertOk(response, "Fetch portal domain");
  const json = (await response.json()) as { data?: unknown };
  assertPortalDomainProjection(json.data);
  return json.data;
}

export async function updatePortalDomain(
  patch: PortalDomainPatch,
): Promise<PortalDomainProjection> {
  const response = await apiPatch("/api/tenant/branding/portal-domain", patch);
  await assertOk(response, "Update portal domain");
  const json = (await response.json()) as { data?: unknown };
  assertPortalDomainProjection(json.data);
  return json.data;
}

export async function verifyPortalDomain(): Promise<PortalDomainProjection> {
  const response = await apiPost("/api/tenant/branding/portal-domain/verify", {});
  await assertOk(response, "Verify portal domain");
  const json = (await response.json()) as { data?: unknown };
  assertPortalDomainProjection(json.data);
  return json.data;
}
